/**
 * Построение баров агрегата из минутных баров компонентов — общий код для data-api
 * и независимой проверки (браузер, консольная утилита).
 *
 * Шкала игровых баров: бар k соответствует рыночной минуте p0MarketTime + (k+1)·1 мин;
 * k = −1 — бар P₀, k = 3 — целевая свеча. Время бара на игровой шкале: startAt + k·minuteMs.
 */
import { aggregateBar, EW3, MINUTE, type AggregateBar, type IndexFormula, type MinuteBar } from './formula.ts';

/** Доступ к минутным барам одной акции. */
export interface BarLookup {
  /** бар, начинающийся ровно в момент t */
  get(t: number): MinuteBar | undefined;
  /** последний бар с началом ≤ t */
  atOrBefore(t: number): MinuteBar | undefined;
}

/** Реализация BarLookup поверх отсортированного массива (для проверки по выгрузке). */
export function lookupFromArray(bars: MinuteBar[]): BarLookup {
  const sorted = [...bars].sort((a, b) => a.t - b.t);
  const byTime = new Map(sorted.map((b) => [b.t, b]));
  return {
    get: (t) => byTime.get(t),
    atOrBefore: (t) => {
      let lo = 0;
      let hi = sorted.length - 1;
      let ans: MinuteBar | undefined;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (sorted[mid]!.t <= t) {
          ans = sorted[mid];
          lo = mid + 1;
        } else hi = mid - 1;
      }
      return ans;
    },
  };
}

/** Рыночная минута игрового бара k. */
export function marketMinuteOf(p0MarketTime: number, k: number): number {
  return p0MarketTime + (k + 1) * MINUTE;
}

/**
 * Бар акции в минуту m. Если сделок не было — последняя известная цена закрытия
 * (O = H = L = C, объём 0); если и её нет — fallback (базовая цена).
 */
export function componentAt(lookup: BarLookup, m: number, fallback: number | null): MinuteBar | null {
  const exact = lookup.get(m);
  if (exact) return exact;
  const p = lookup.atOrBefore(m)?.c ?? fallback;
  if (p === null || p === undefined) return null;
  return { t: m, o: p, h: p, l: p, c: p, v: 0 };
}

export interface SeriesParams {
  p0MarketTime: number;
  startAt: number;
  minuteMs: number;
  basePrices: number[];
  avgVolumes: number[];
}

/** Бар агрегата k по барам компонентов (в порядке состава). */
export function aggregateAt(lookups: BarLookup[], p: SeriesParams, k: number, f: IndexFormula = EW3): AggregateBar {
  const m = marketMinuteOf(p.p0MarketTime, k);
  const comps = lookups.map((l, i) => componentAt(l, m, p.basePrices[i]!)!);
  return aggregateBar(p.startAt + k * p.minuteMs, comps, p.basePrices, p.avgVolumes, f);
}
